import jsPDF from "jspdf";

// Lightweight PDF generator for the Site Survey feature.
//
// Produces a clean "Site Visit Report" PDF with:
//   - Cover page (project metadata)
//   - Optional general notes
//   - One section per room: voice-note transcripts, typed notes, work items
//     and thumbnail photos.
//
// Photos are fetched from object storage URLs and embedded inline. Anything
// that fails to load is silently skipped so a single broken image does not
// blow up the whole report.

interface VoiceNote {
  id: string;
  transcript: string;
  durationSec?: number;
  createdAt: string;
}

interface PhotoEntry {
  id: string;
  objectPath: string;
  caption?: string;
}

interface RoomEntry {
  id: string;
  name: string;
  voiceNotes?: VoiceNote[];
  typedNotes?: string;
  workItems?: string[];
  photos?: PhotoEntry[];
}

interface SurveyProject {
  clientName?: string;
  projectAddress?: string;
  projectType?: string;
  description?: string;
  capturedBy?: string;
  visitDate?: string;
}

interface GenerateOptions {
  project?: SurveyProject;
  rooms?: RoomEntry[];
  generalNotes?: string;
}

const PAGE_W = 210; // A4 mm
const PAGE_H = 297;
const MARGIN = 18;
const CONTENT_W = PAGE_W - MARGIN * 2;

const PROJECT_TYPE_LABELS: Record<string, string> = {
  extension: "Extension",
  loft: "Loft Conversion",
  renovation: "Renovation / Refurbishment",
  "new-build": "New Build",
  kitchen: "Kitchen Refit",
  bathroom: "Bathroom Refit",
  "garage-conversion": "Garage Conversion",
  outbuilding: "Outbuilding / Annexe",
  external: "External Works",
  structural: "Structural Works",
  maintenance: "Maintenance / Repairs",
  other: "Other",
};

function formatDate(iso?: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function projectTypeLabel(value?: string): string {
  if (!value) return "";
  return PROJECT_TYPE_LABELS[value] || value;
}

// Load an object storage URL into a base64 data URL with intrinsic dimensions.
async function loadImage(
  url: string
): Promise<{ dataUrl: string; w: number; h: number; format: "JPEG" | "PNG" } | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
    const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => reject(new Error("image decode failed"));
      img.src = dataUrl;
    });
    const format: "JPEG" | "PNG" = blob.type.includes("png") ? "PNG" : "JPEG";
    return { dataUrl, w: dims.w, h: dims.h, format };
  } catch {
    return null;
  }
}

export async function generateSiteSurveyPdf(opts: GenerateOptions): Promise<Blob> {
  const { project = {}, rooms = [], generalNotes } = opts;
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  let y = MARGIN;

  // ── Cover ──────────────────────────────────────────────────────────────
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("SITE VISIT REPORT", MARGIN, y);
  y += 10;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(110, 110, 110);
  doc.text("Captured on site for use as a project brief.", MARGIN, y);
  y += 8;
  doc.setTextColor(0, 0, 0);

  // Metadata table
  const meta: Array<[string, string]> = [
    ["Site address", project.projectAddress || "—"],
    ["Client", project.clientName || "—"],
    ["Project type", projectTypeLabel(project.projectType) || "—"],
    ["Visit date", formatDate(project.visitDate) || "—"],
    ["Captured by", project.capturedBy || "—"],
    ["Rooms surveyed", String(rooms.length)],
  ];
  doc.setFontSize(10);
  for (const [label, value] of meta) {
    if (y > PAGE_H - MARGIN - 12) {
      doc.addPage();
      y = MARGIN;
    }
    doc.setFont("helvetica", "bold");
    doc.text(label, MARGIN, y);
    doc.setFont("helvetica", "normal");
    const lines = doc.splitTextToSize(value, CONTENT_W - 45);
    doc.text(lines, MARGIN + 45, y);
    y += Math.max(6, lines.length * 5);
  }

  if (project.description) {
    y += 4;
    y = ensureSpace(doc, y, 12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Project description", MARGIN, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(project.description, CONTENT_W);
    for (const line of lines) {
      y = ensureSpace(doc, y, 5);
      doc.text(line, MARGIN, y);
      y += 5;
    }
  }

  if (generalNotes && generalNotes.trim()) {
    y += 4;
    y = ensureSpace(doc, y, 12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("General notes", MARGIN, y);
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const lines = doc.splitTextToSize(generalNotes, CONTENT_W);
    for (const line of lines) {
      y = ensureSpace(doc, y, 5);
      doc.text(line, MARGIN, y);
      y += 5;
    }
  }

  // ── Rooms ──────────────────────────────────────────────────────────────
  for (let i = 0; i < rooms.length; i++) {
    const room = rooms[i];
    // Each room starts on a new page so it's easy to skim.
    doc.addPage();
    y = MARGIN;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`Room ${i + 1}: ${room.name}`, MARGIN, y);
    y += 8;

    // Quick counts strip
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(110, 110, 110);
    const counts = [
      `${room.voiceNotes?.length || 0} voice note${(room.voiceNotes?.length || 0) === 1 ? "" : "s"}`,
      `${room.photos?.length || 0} photo${(room.photos?.length || 0) === 1 ? "" : "s"}`,
      `${room.workItems?.length || 0} work item${(room.workItems?.length || 0) === 1 ? "" : "s"}`,
    ];
    doc.text(counts.join("  ·  "), MARGIN, y);
    y += 8;
    doc.setTextColor(0, 0, 0);

    // Voice transcripts
    if (room.voiceNotes && room.voiceNotes.length > 0) {
      y = sectionHeading(doc, y, "Voice notes (transcribed)");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      for (let n = 0; n < room.voiceNotes.length; n++) {
        const note = room.voiceNotes[n];
        const heading = `Note ${n + 1}${note.durationSec ? ` (${formatDuration(note.durationSec)})` : ""}`;
        y = ensureSpace(doc, y, 6);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.text(heading, MARGIN, y);
        y += 5;
        doc.setFont("helvetica", "normal");
        const text = note.transcript?.trim() || "(no transcript)";
        const lines = doc.splitTextToSize(text, CONTENT_W);
        for (const line of lines) {
          y = ensureSpace(doc, y, 5);
          doc.text(line, MARGIN, y);
          y += 5;
        }
        y += 2;
      }
    }

    // Typed notes
    if (room.typedNotes && room.typedNotes.trim()) {
      y = sectionHeading(doc, y, "Typed notes");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const lines = doc.splitTextToSize(room.typedNotes, CONTENT_W);
      for (const line of lines) {
        y = ensureSpace(doc, y, 5);
        doc.text(line, MARGIN, y);
        y += 5;
      }
    }

    // Work items
    if (room.workItems && room.workItems.length > 0) {
      y = sectionHeading(doc, y, "Work items spotted");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      for (const item of room.workItems) {
        y = ensureSpace(doc, y, 5);
        const lines = doc.splitTextToSize(`•  ${item}`, CONTENT_W);
        for (const line of lines) {
          y = ensureSpace(doc, y, 5);
          doc.text(line, MARGIN, y);
          y += 5;
        }
      }
    }

    // Photos
    if (room.photos && room.photos.length > 0) {
      y = sectionHeading(doc, y, `Photos (${room.photos.length})`);
      // Two-column thumbnail grid
      const colW = (CONTENT_W - 6) / 2;
      const thumbH = 60; // mm
      let col = 0;
      let rowY = y;
      for (const photo of room.photos) {
        const loaded = await loadImage(photo.objectPath);
        if (!loaded) continue;

        // Constrain to max dims while preserving aspect ratio
        const ratio = loaded.w / loaded.h;
        let drawW = colW;
        let drawH = colW / ratio;
        if (drawH > thumbH) {
          drawH = thumbH;
          drawW = thumbH * ratio;
        }

        const cellHeight = thumbH + (photo.caption ? 8 : 4);
        if (rowY + cellHeight > PAGE_H - MARGIN) {
          doc.addPage();
          rowY = MARGIN;
          col = 0;
        }

        const x = MARGIN + col * (colW + 6);
        try {
          doc.addImage(loaded.dataUrl, loaded.format, x, rowY, drawW, drawH);
        } catch {
          // skip broken image
        }

        if (photo.caption) {
          doc.setFont("helvetica", "italic");
          doc.setFontSize(9);
          doc.setTextColor(80, 80, 80);
          const captionLines = doc.splitTextToSize(photo.caption, colW);
          doc.text(captionLines, x, rowY + thumbH + 4);
          doc.setTextColor(0, 0, 0);
        }

        col += 1;
        if (col >= 2) {
          col = 0;
          rowY += cellHeight;
        }
      }
      y = col === 0 ? rowY : rowY + thumbH + 8;
    }
  }

  // ── Footer page numbers ───────────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(
      `Site Visit Report  ·  Page ${p} of ${pageCount}`,
      PAGE_W / 2,
      PAGE_H - 8,
      { align: "center" }
    );
    doc.setTextColor(0, 0, 0);
  }

  return doc.output("blob");
}

function ensureSpace(doc: jsPDF, y: number, needed: number): number {
  if (y + needed > PAGE_H - MARGIN) {
    doc.addPage();
    return MARGIN;
  }
  return y;
}

function sectionHeading(doc: jsPDF, y: number, text: string): number {
  y = ensureSpace(doc, y + 2, 10);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(40, 40, 40);
  doc.text(text, MARGIN, y);
  doc.setTextColor(0, 0, 0);
  return y + 6;
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
